import InputField from "components/fields/InputField";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Turnstile from "react-turnstile";
import { API_URL } from "utils/config";
import axios from "axios";
import toast from "react-hot-toast";
import { signUpAction } from "../../redux/action/index";
import { getCookie } from "utils/cookie";

export default function SignUp() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [captcha, setCaptcha] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [loading, setLoading] = useState(false); // New loading state
  const login = getCookie("token") ? true : false;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (login) {
      navigate("/admin/default");
    }
  }, [login, navigate]);

  // Validation functions -- start
  const validateFirstName = (firstName) => {
    if (!firstName.trim()) {
      setFirstNameError("First Name is required.");
      return false;
    }
    setFirstNameError("");
    return true;
  };

  const validateLastName = (lastName) => {
    if (!lastName.trim()) {
      setLastNameError("Last Name is required.");
      return false;
    }
    setLastNameError("");
    return true;
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      setEmailError("Email is required.");
      return false;
    } else if (!emailRegex.test(email)) {
      setEmailError("Invalid email format.");
      return false;
    }
    setEmailError("");
    return true;
  };

  const validatePassword = (password) => {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{12,}$/;
    if (!passwordRegex.test(password)) {
      setPasswordError(
        "Password must be at least 12 characters long, include one special character, one number, one lowercase, and one uppercase letter."
      );
      return false;
    }
    setPasswordError("");
    return true;
  };

  const validateConfirmPassword = (confirmPassword) => {
    if (confirmPassword !== password) {
      setConfirmPasswordError("Passwords do not match.");
      return false;
    }
    setConfirmPasswordError("");
    return true;
  };

  const validatePhoneNumber = (phoneNumber) => {
    const phoneRegex = /^\d{10}$/; // Only 10 digits
    if (!phoneRegex.test(phoneNumber)) {
      setPhoneNumberError(
        "Phone number must be 10 digits long and contain only numbers."
      );
      return false;
    }
    setPhoneNumberError("");
    return true;
  };

  // Validation functions -- close

  // captcha function
  const handleCaptchaChange = async (value) => {
    try {
      const body = {
        captcha: true,
        g_recaptcha_response: value,
      };
      const response = await axios.post(API_URL + "/auth/verify_captcha", body);
      if (response?.data?.status === 200) {
        setCaptcha(true);
        setCaptchaVerified(true);
      } else {
        toast.error(response?.data?.detail[0]?.msg);
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleRegister = () => {
    const isFirstNameValid = validateFirstName(firstName);
    const isLastNameValid = validateLastName(lastName);
    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);
    const isConfirmPasswordValid = validateConfirmPassword(confirmPassword);
    const isPhoneNumberValid = validatePhoneNumber(phoneNumber);

    if (
      isFirstNameValid &&
      isLastNameValid &&
      isEmailValid &&
      isPasswordValid &&
      isConfirmPasswordValid &&
      isPhoneNumberValid &&
      captchaVerified
    ) {
      setLoading(true); // Set loading to true before making the API call
      try {
        dispatch(
          signUpAction({
            firstname: firstName,
            lastname: lastName,
            email: email,
            password: password,
            passwordConfirm: confirmPassword,
            role: "user",
            username: email,
            phone: phoneNumber,
          })
        ).then((response) => {
          setLoading(false); // Set loading to false after the API call
          if (response !== undefined) {
            navigate("/auth/sign-in");
          }
        });
      } catch (error) {
        setLoading(false); // Set loading to false if there's an error
        console.log("Sign Up err:", error);
      }
    } else {
      console.log("Validation errors");
    }
  };

  return (
    <div className="mb-16 mt-10 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center lg:justify-start">
      <div className="mt-[1vh] w-full max-w-full flex-col items-center md:pl-4 lg:pl-0 xl:max-w-[420px]">
        <h4 className="mb-2.5 text-4xl font-bold text-navy-700 dark:text-white">
          Register
        </h4>
        <p className="mb-6 ml-1 text-base text-gray-600">
          Please fill in this form to create an account!
        </p>

        {/* First and Last Name */}
        <div className="mb-1 flex w-full gap-4">
          <div className="w-1/2">
            <InputField
              variant="auth"
              extra="mb-1"
              label="First Name*"
              placeholder="First Name"
              id="first-name"
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                validateFirstName(e.target.value);
              }}
              type="text"
            />
            {firstNameError && (
              <p className="ml-1.5 text-sm text-red-500">{firstNameError}</p>
            )}
          </div>
          <div className="w-1/2">
            <InputField
              variant="auth"
              extra="mb-1"
              label="Last Name*"
              placeholder="Last Name"
              id="last-name"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                validateLastName(e.target.value);
              }}
              type="text"
            />
            {lastNameError && (
              <p className="ml-1.5 text-sm text-red-500">{lastNameError}</p>
            )}
          </div>
        </div>

        {/* Email */}
        <div className="mb-1 w-full">
          <InputField
            variant="auth"
            extra="mb-1"
            label="Email*"
            placeholder="Email address"
            id="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              validateEmail(e.target.value);
            }}
            type="text"
          />
          {emailError && (
            <p className="ml-1.5 text-sm text-red-500">{emailError}</p>
          )}
        </div>

        {/* Phone Number */}
        <div className="mb-1 w-full">
          <InputField
            variant="auth"
            extra="mb-1"
            label="Phone Number"
            placeholder="XXXXXXXXXX"
            id="phone-number"
            value={phoneNumber}
            onChange={(e) => {
              setPhoneNumber(e.target.value);
              validatePhoneNumber(e.target.value);
            }}
            type="text"
          />
          {phoneNumberError && (
            <p className="ml-1.5 text-sm text-red-500">{phoneNumberError}</p>
          )}
        </div>

        {/* Password */}
        <div className="mb-1 w-full">
          <InputField
            variant="auth"
            extra="mb-1"
            label="Password*"
            placeholder="Min. 12 characters"
            id="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              validatePassword(e.target.value);
            }}
            type="password"
            boxType="password"
          />
          {passwordError && (
            <p className="ml-1.5 text-sm text-red-500">{passwordError}</p>
          )}
        </div>

        {/* Confirm Password */}
        <div className="mb-1 w-full">
          <InputField
            variant="auth"
            extra="mb-1"
            label="Confirm Password*"
            placeholder="Min. 12 characters"
            id="confirm-password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              validateConfirmPassword(e.target.value);
            }}
            type="password"
            boxType="password"
          />
          {confirmPasswordError && (
            <p className="ml-1.5 text-sm text-red-500">
              {confirmPasswordError}
            </p>
          )}
        </div>

        <div className="mb-1 w-full">
          <Turnstile
            sitekey="0x4AAAAAAAkH86FYslmjxCH3"
            // test key
            // sitekey="1x00000000000000000000AA"
            className="mt-2"
            onVerify={(token) => {
              handleCaptchaChange(token);
              // setCaptchaVerified(true);
              // setCaptcha(true);
            }}
          />
        </div>
        <button
          disabled={loading || !captchaVerified || !captcha}
          onClick={() => handleRegister()}
          className="linear   mt-2 w-full rounded-xl bg-brand-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 disabled:cursor-not-allowed disabled:bg-gray-600 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
        >
          {loading ? "Signing Up..." : "Sign Up"}
        </button>
        <div className="mt-4">
          <span className=" text-sm font-medium text-navy-700 dark:text-gray-600">
            Already registered?
          </span>

          <a
            href="/auth/sign-in"
            className="ml-1 text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-white"
          >
            Sign In
          </a>
        </div>
      </div>
    </div>
  );
}
